import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import TimeSelect from '../components/TimeSelect';
import AutoExpandTextarea from '../components/AutoExpandTextarea';
import '../styles/NewReport.css';

function EditReport() {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    shift_date: '',
    shift_start_time: '',
    shift_end_time: '',
    free_text: '',
    checklist_comment: ''
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [checklistResponses, setChecklistResponses] = useState([]);
  const [checklistComment, setChecklistComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { token, user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      const report = await api.getReport(token, id);

      // Check if user can edit this report
      if (!isAdmin && report.user_id !== user?.id) {
        setError('Du har ikke tilgang til å redigere denne rapporten');
        setLoading(false);
        return;
      }

      setFormData({
        shift_date: report.shift_date || '',
        shift_start_time: report.shift_start_time || '',
        shift_end_time: report.shift_end_time || '',
        free_text: report.free_text || '',
        checklist_comment: report.checklist_comment || ''
      });

      // Load template and checklist data if available
      if (report.template_id) {
        const template = await api.getTemplate(token, report.template_id);
        setSelectedTemplate(template);

        // Initialize checklist responses from existing data
        if (report.checklist_responses && report.checklist_responses.length > 0) {
          setChecklistResponses(report.checklist_responses.map(r => ({
            id: r.id,
            item_id: r.item_id,
            is_checked: r.is_checked === 1,
            notes: r.notes || ''
          })));
        }
      }

      setChecklistComment(report.checklist_comment || '');
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCheckboxChange = (itemId, checked) => {
    setChecklistResponses(prev =>
      prev.map(response =>
        response.item_id === itemId
          ? { ...response, is_checked: checked }
          : response
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation: If any checklist item is unchecked, require comment
    if (checklistResponses.length > 0) {
      const hasUnchecked = checklistResponses.some(response => !response.is_checked);
      if (hasUnchecked && !checklistComment.trim()) {
        setError('Kommentar er påkrevd når et eller flere punkter ikke er krysset av');
        return;
      }
    }

    setSaving(true);

    try {
      const reportData = {
        ...formData,
        checklist_comment: checklistComment || null,
        checklist_responses: checklistResponses.length > 0 ? checklistResponses : null
      };
      await api.updateReport(token, id, reportData);
      navigate(`/reports/${id}`);
    } catch (err) {
      setError(err.message || 'Kunne ikke oppdatere rapporten');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Laster rapport...</div>;
  }

  if (error && !formData.shift_date) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/reports')} className="btn-primary">
          Tilbake til rapporter
        </button>
      </div>
    );
  }

  return (
    <div className="new-report-container">
      <div className="report-header">
        <button onClick={() => navigate(`/reports/${id}`)} className="btn-back">
          ← Tilbake
        </button>
        <h1>Rediger skiftrapport</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="report-form">
        <div className="form-section">
          <h3>Skiftinformasjon</h3>

          <div className="form-group">
            <label htmlFor="shift_date">Dato (skiftet startet) *</label>
            <input
              type="date"
              id="shift_date"
              value={formData.shift_date}
              onChange={(e) => setFormData({ ...formData, shift_date: e.target.value })}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shift_start_time">Starttid</label>
              <TimeSelect
                id="shift_start_time"
                value={formData.shift_start_time}
                onChange={(e) => setFormData({ ...formData, shift_start_time: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="shift_end_time">Sluttid</label>
              <TimeSelect
                id="shift_end_time"
                value={formData.shift_end_time}
                onChange={(e) => setFormData({ ...formData, shift_end_time: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Fritekst rapport ("Boka") *</h3>
          <div className="form-group">
            <AutoExpandTextarea
              id="free_text"
              minRows={8}
              value={formData.free_text}
              onChange={(e) => setFormData({ ...formData, free_text: e.target.value })}
              placeholder="Skriv litt om hvordan skiftet var og dersom du har beskjeder til neste assistent"
              required
            />
          </div>
        </div>

        {selectedTemplate && selectedTemplate.items && selectedTemplate.items.length > 0 && (
          <div className="form-section">
            <div className="checklist-items">
              {selectedTemplate.items.map((item) => {
                const response = checklistResponses.find(r => r.item_id === item.id);
                return (
                  <div key={item.id} className="checklist-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={response?.is_checked || false}
                        onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
                      />
                      <span>{item.item_text}</span>
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label htmlFor="checklist_comment">
                Kommentar {checklistResponses.some(r => !r.is_checked) && <span style={{ color: 'var(--danger-color)' }}>*</span>}
              </label>
              <AutoExpandTextarea
                id="checklist_comment"
                minRows={4}
                value={checklistComment}
                onChange={(e) => setChecklistComment(e.target.value)}
                placeholder="Skriv kommentar her (påkrevd hvis noen punkter ikke er krysset av)..."
              />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(`/reports/${id}`)}
            className="btn-secondary"
          >
            Avbryt
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditReport;
